<template>
  <section class="vm-snapshot-page">
    <div class="app-page-intro">
      <h2>VM Snapshots</h2>
      <p>
        This page shows libvirt/qcow2 snapshots for
        <strong>{{ vmName }}</strong>.
      </p>
    </div>

    <div class="panel panel-default vm-snapshot-page__toolbar">
      <button
        type="button"
        class="btn btn-primary"
        :disabled="actionsDisabled || creating"
        :title="actionsDisabled ? 'Disabled while VM is running' : ''"
        @click="openCreateModal"
      >
        Create Snapshot
      </button>
      <a class="btn btn-default" :href="backUrl">Back to VM list</a>
    </div>

    <div class="panel panel-default vm-snapshot-page__disk">
      <h4>Current VM Disk</h4>
      <p class="text-muted vm-snapshot-page__disk-text">
        Current disk size:
        <strong>{{ humanDiskSize }}</strong>
        <template v-if="diskSizeBytes !== null">
          ({{ formatNumber(diskSizeBytes) }} bytes)
        </template>
      </p>
    </div>

    <div class="panel panel-default vm-snapshot-page__table-panel">
      <h4>Snapshots</h4>
      <div class="table-wrapper-scroll-y app-scroll-table">
        <table class="table table-striped table-bordered table-condensed">
          <thead>
            <tr>
              <th>Name</th>
              <th>Created At (UTC)</th>
              <th>State</th>
              <th>Current</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="snapshots.length === 0">
              <td colspan="6" class="text-center text-muted">No snapshots found.</td>
            </tr>
            <tr v-for="snap in snapshots" :key="snap.name">
              <td>
                <span :style="treeIndentStyle(snap.tree_depth)">
                  {{ Number(snap.tree_depth || 0) > 0 ? '└ ' : '' }}{{ snap.name }}
                </span>
              </td>
              <td>{{ snap.created_at || '-' }}</td>
              <td>{{ snap.state || '-' }}</td>
              <td>{{ snap.is_current ? 'Yes' : 'No' }}</td>
              <td>{{ snap.description || '-' }}</td>
              <td class="vm-snapshot-page__actions">
                <button
                  type="button"
                  class="btn btn-warning btn-sm"
                  :disabled="actionsDisabled || loading"
                  :title="actionsDisabled ? 'Disabled while VM is running' : ''"
                  @click="revertSnapshot(snap.name)"
                >
                  Revert
                </button>
                <button
                  type="button"
                  class="btn btn-danger btn-sm"
                  :disabled="actionsDisabled || loading"
                  :title="actionsDisabled ? 'Disabled while VM is running' : ''"
                  @click="deleteSnapshot(snap.name)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="createModalOpen" class="vm-snapshot-modal-backdrop" @click="closeCreateModal">
      <div class="vm-snapshot-modal" role="dialog" aria-modal="true" aria-label="Create VM Snapshot" @click.stop>
        <div v-if="creating" class="vm-snapshot-modal__progress">
          <span class="vm-list-vue-results-spinner" aria-hidden="true"></span>
          <span>{{ progressText || 'Creating snapshot...' }}</span>
        </div>
        <h4>Create Snapshot</h4>
        <p class="text-muted" style="margin-bottom: 8px;">{{ vmName }}</p>
        <div class="form-group">
          <label>Snapshot name</label>
          <input v-model="snapshotName" :disabled="creating" type="text" class="form-control" placeholder="snap-20260518-1205" />
        </div>
        <div class="form-group" style="margin-top: 8px;">
          <label>Description (optional)</label>
          <input v-model="snapshotDescription" :disabled="creating" type="text" class="form-control" placeholder="before package update" />
        </div>
        <div style="margin-top: 12px; display: flex; gap: 8px; justify-content: flex-end;">
          <button type="button" class="btn btn-default" :disabled="creating" @click="closeCreateModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="creating" @click="createSnapshot">{{ creating ? 'Creating...' : 'Create' }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({})
  }
})

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const vmId = props.initialData?.vm_id
const vmName = props.initialData?.vm_name || 'VM'
const vmState = ref(props.initialData?.vm_state)
const diskSizeBytes = ref(Number.isFinite(Number(props.initialData?.current_disk_size_bytes)) ? Number(props.initialData.current_disk_size_bytes) : null)
const snapshots = ref(Array.isArray(props.initialData?.snapshots) ? props.initialData.snapshots : [])
const endpoints = props.initialData?.endpoints || {}
const backUrl = props.initialData?.back_url || '/vms/list'

const createModalOpen = ref(false)
const snapshotName = ref(defaultSnapshotName())
const snapshotDescription = ref('')
const creating = ref(false)
const loading = ref(false)
const progressText = ref('')

const actionsDisabled = computed(() => vmState.value === 'up')
const humanDiskSize = computed(() => {
  if (diskSizeBytes.value === null) return 'N/A'
  return formatBytes(diskSizeBytes.value)
})

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

function treeIndentStyle(depth) {
  const level = Number(depth || 0)
  return { paddingLeft: `${level * 18}px` }
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0))
}

function formatBytes(bytes) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let value = Number(bytes || 0)
  let idx = 0
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024
    idx += 1
  }
  return `${value.toFixed(value >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`
}

function defaultSnapshotName() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  return `snap-${y}${m}${d}-${hh}${mm}`
}

function openCreateModal() {
  if (actionsDisabled.value || creating.value) return
  createModalOpen.value = true
  if (!String(snapshotName.value || '').trim()) snapshotName.value = defaultSnapshotName()
}

function closeCreateModal() {
  if (creating.value) return
  createModalOpen.value = false
}

async function requestJson(url, options = {}) {
  const headers = new Headers(options.headers || {})
  headers.set('X-Requested-With', 'XMLHttpRequest')
  headers.set('Accept', 'application/json')
  if (csrfToken) headers.set('X-CSRF-Token', csrfToken)

  const response = await fetch(url, { ...options, headers, credentials: 'same-origin' })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(payload?.message || 'Request failed.')
  }
  return payload
}

async function refreshSnapshots() {
  if (!endpoints.list_json_url) return
  loading.value = true
  try {
    const payload = await requestJson(endpoints.list_json_url, { method: 'GET', cache: 'no-store' })
    snapshots.value = Array.isArray(payload?.snapshots) ? payload.snapshots : []
    vmState.value = payload?.vm_state || vmState.value
    diskSizeBytes.value = Number.isFinite(Number(payload?.current_disk_size_bytes)) ? Number(payload.current_disk_size_bytes) : null
  } finally {
    loading.value = false
  }
}

async function createSnapshot() {
  if (!endpoints.create_url) return

  const name = String(snapshotName.value || '').trim() || defaultSnapshotName()
  try {
    creating.value = true
    progressText.value = `Creating snapshot for ${vmName}...`

    const body = new URLSearchParams()
    body.set('snapshot_name', name)
    if (snapshotDescription.value) body.set('description', snapshotDescription.value)

    const payload = await requestJson(endpoints.create_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString()
    })

    sccShowToast(payload?.level || 'success', payload?.message || 'Snapshot created.')
    createModalOpen.value = false
    snapshotName.value = defaultSnapshotName()
    snapshotDescription.value = ''
    await refreshSnapshots()
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to create snapshot.')
  } finally {
    creating.value = false
    progressText.value = ''
  }
}

async function revertSnapshot(name) {
  if (!endpoints.revert_url || actionsDisabled.value) return
  const confirmed = window.confirm(`Revert ${vmName} to snapshot ${name}? Current disk state will be lost.`)
  if (!confirmed) return

  try {
    loading.value = true
    const body = new URLSearchParams()
    body.set('snapshot_name', name)
    body.set('force', 'true')

    const payload = await requestJson(endpoints.revert_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString()
    })

    sccShowToast(payload?.level || 'success', payload?.message || `Reverted VM to snapshot: ${name}`)
    await refreshSnapshots()
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to revert snapshot.')
  } finally {
    loading.value = false
  }
}

async function deleteSnapshot(name) {
  if (!endpoints.delete_url || actionsDisabled.value) return
  const confirmed = window.confirm(`Delete snapshot ${name}?`)
  if (!confirmed) return

  try {
    loading.value = true
    const body = new URLSearchParams()
    body.set('snapshot_name', name)
    body.set('delete_children', 'false')

    const payload = await requestJson(endpoints.delete_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString()
    })

    sccShowToast(payload?.level || 'success', payload?.message || `Deleted snapshot: ${name}`)
    await refreshSnapshots()
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to delete snapshot.')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.vm-snapshot-page__toolbar,
.vm-snapshot-page__disk,
.vm-snapshot-page__table-panel {
  padding: 16px;
  margin-bottom: 16px;
}

.vm-snapshot-page__toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
}

.vm-snapshot-page__disk-text {
  margin: 0;
}

.vm-snapshot-page__actions {
  white-space: nowrap;
}

.vm-snapshot-page__actions .btn + .btn {
  margin-left: 6px;
}

.vm-snapshot-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2200;
  background: rgba(16, 22, 38, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.vm-snapshot-modal {
  width: min(520px, calc(100vw - 24px));
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 24px 56px rgba(0, 0, 0, 0.3);
  padding: 16px;
}

.vm-snapshot-modal__progress {
  margin-bottom: 10px;
  border: 1px solid #cfdcf8;
  border-radius: 7px;
  background: #eef4ff;
  color: #1f3f73;
  font-weight: 600;
  font-size: 12px;
  padding: 7px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
